import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import './App.css';
import SurveyComponent from './components/SurveyComponent';
import { UnifiedLogin } from './components/UnifiedLogin';
import { UserDashboard } from './components/UserDashboard';
import { SurveyProtectedRoute } from './components/SurveyProtectedRoute';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { AdminLayout } from './components/admin/AdminLayout';
import { AdminDashboard } from './components/admin/AdminDashboard';
import { SurveyDetailView } from './components/admin/SurveyDetailView';
import { SurveysList } from './components/admin/SurveysList';
import { UsersList } from './components/admin/UsersList';
import { SMTPConfigList } from './components/admin/SMTPConfigList';
import { ProtectedRoute } from './components/admin/ProtectedRoute';
import { ReportViewTest } from './components/ReportViewTest';
import EmailVerification from './components/EmailVerification';
import { ResetPassword } from './components/ResetPassword';

// Component to handle automatic navigation after login
const AuthNavigationHandler: React.FC = () => {
	const { user, isAuthenticated, isLoading } = useAuth();
	const navigate = useNavigate();
	const location = useLocation();

	useEffect(() => {
		if (!isLoading && isAuthenticated && user) {
			// Only redirect if user is on the login page (/)
			// This allows users to navigate to other routes like /survey without being redirected
			if (location.pathname === '/') {
				// Check if user is admin
				if (user.type === 'admin') {
					// Admin user - redirect to admin dashboard
					navigate('/admin', { replace: true });
				} else {
					// Regular user - redirect to user dashboard
					navigate('/dashboard', { replace: true });
				}
			}
		}
	}, [user, isAuthenticated, isLoading, location.pathname, navigate]);

	return null;
};

function AppContent() {
	return (
		<div className='App'>
			<AuthNavigationHandler />
			<Routes>
				{/* Main login/welcome route */}
				<Route path='/' element={<UnifiedLogin />} />

				{/* Email verification route */}
				<Route path='/verify-email' element={<EmailVerification />} />

				{/* Reset password route */}
				<Route path='/reset-password' element={<ResetPassword />} />

				{/* User dashboard - protected for regular users */}
				<Route
					path='/dashboard'
					element={
						<SurveyProtectedRoute>
							<UserDashboard />
						</SurveyProtectedRoute>
					}
				/>

				{/* Protected survey route */}
				<Route
					path='/survey'
					element={
						<SurveyProtectedRoute>
							<SurveyComponent />
						</SurveyProtectedRoute>
					}
				/>
				<Route
					path='/surveys/:surveyId'
					element={
						<SurveyProtectedRoute>
							<SurveyDetailView />
						</SurveyProtectedRoute>
					}
				/>

				{/* Test route for report view */}
				<Route path='/test-report-view' element={<ReportViewTest />} />

				{/* Protected admin routes */}
				<Route
					path='/admin'
					element={
						<ProtectedRoute>
							<AdminLayout />
						</ProtectedRoute>
					}
				>
					<Route index element={<AdminDashboard />} />
					<Route path='surveys' element={<SurveysList />} />
					<Route path='users' element={<UsersList />} />
					<Route path='smtp' element={<SMTPConfigList />} />
					<Route path='surveys/:surveyId' element={<SurveyDetailView />} />
				</Route>

				{/* Redirect any unmatched routes */}
				<Route path='*' element={<Navigate to='/' replace />} />
			</Routes>
		</div>
	);
}

function App() {
	return (
		<AuthProvider>
			<Router>
				<AppContent />
			</Router>
		</AuthProvider>
	);
}

export default App;
